import { useBlocker } from 'react-router-dom';

import { useGlobalModalsContext } from 'contexts/globalModalsContext';
import Button from 'ui/Button';
import Dialog from 'ui/Dialog';
import { Text } from 'ui/Typography';
import { useDiagramCache } from 'utils/caching';
import { useCurrentRouteSankeyId } from 'utils/routes';
import { useCurrentUser } from 'utils/user';

// This component prompts the user with a confirmation dialog when they attempt
// to navigate away from a Sankey diagram that has unsaved changes. It uses the
// `useBlocker` hook to intercept navigation events and checks if there are
// unsaved changes in the diagram cache.
// - This can only happen for the users who don't have autosave enabled
//   (mainly non-customers)
// - We don't need to show this prompt if the user is in the conversion modal since
//   their changes are already being saved or they are in the process of converting
//   their diagram
// - We don't need to show this prompt if closing the tab or browser, since their
//   diagram will be reconstructed from the cache when they return to the page
const UnsavedDiagramChangesConditionalPrompt: React.FC = () => {
  const { user } = useCurrentUser();
  const sankeyId = useCurrentRouteSankeyId();
  const sankeyCache = useDiagramCache(sankeyId);
  const { isAuthOrConversionModalOpen } = useGlobalModalsContext();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname &&
      !isAuthOrConversionModalOpen &&
      !user?.can_update_sankeys &&
      sankeyCache.exists()
  );

  return (
    <Dialog.Root
      open={blocker.state === 'blocked'}
      onOpenChange={blocker.reset}
      role="alertdialog"
    >
      <Dialog.Content>
        <Dialog.Header>Unsaved changes</Dialog.Header>
        <Dialog.Body>
          <Text>
            Are you sure you want to leave before saving your work? If you do
            so, all your changes to this diagram will be lost.
          </Text>
        </Dialog.Body>
        <Dialog.Footer>
          <Button
            variant="danger-ghost"
            onClick={() => {
              sankeyCache.clear();
              blocker.proceed?.();
            }}
          >
            Leave without saving
          </Button>
          <Button variant="primary-solid" onClick={blocker.reset}>
            Cancel
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default UnsavedDiagramChangesConditionalPrompt;
